import { Box, Button, Paper, Typography, Fade, Modal } from "@mui/material";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { RootState } from "../store/Index";

interface TermsModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({ open, onClose, onAccept }) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Fade in={open}>
        <Paper
          sx={{
            width: { xs: "90%", sm: "70%", md: "50%" },
            maxWidth: "600px",
            maxHeight: "80vh",
            overflowY: "auto",
            p: 4,
            borderRadius: "16px",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            boxShadow: `0 20px 60px rgba(${
              reduxTheme.primary_colors?.[400]
                ? parseInt(reduxTheme.primary_colors[400].slice(1, 3), 16)
                : 97
            }, ${
              reduxTheme.primary_colors?.[400]
                ? parseInt(reduxTheme.primary_colors[400].slice(3, 5), 16)
                : 68
            }, ${
              reduxTheme.primary_colors?.[400]
                ? parseInt(reduxTheme.primary_colors[400].slice(5, 7), 16)
                : 56
            }, 0.3)`,
          }}
          component={motion.div}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${
                reduxTheme.primary_colors?.[400] || "#614438"
              } 0%, ${
                reduxTheme.primary_colors?.[300] || "#C29363"
              } 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 3,
            }}
          >
            Terms and Conditions
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: reduxTheme.neutral_colors?.[100] || "#333",
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            By signing up, you agree to the following terms and conditions. Please read carefully:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: reduxTheme.neutral_colors?.[100] || "#333",
              mb: 2,
              lineHeight: 1.6,
            }}
          >
            1. <strong>Acceptance of Terms</strong>: By using our services, you agree to comply with and be bound by these terms and conditions.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: reduxTheme.neutral_colors?.[100] || "#333",
              mb: 2,
              lineHeight: 1.6,
            }}
          >
            2. <strong>Account Responsibilities</strong>: You are responsible for maintaining the confidentiality of your account and password.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: reduxTheme.neutral_colors?.[100] || "#333",
              mb: 2,
              lineHeight: 1.6,
            }}
          >
            3. <strong>Prohibited Activities</strong>: You agree not to engage in any unlawful or prohibited activities while using our services.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: reduxTheme.neutral_colors?.[100] || "#333",
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            4. <strong>Termination</strong>: We reserve the right to terminate or suspend your account for violating these terms.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                textTransform: "none",
                borderColor:
                  reduxTheme.neutral_colors?.[300] || "rgba(0,0,0,0.1)",
                color: reduxTheme.neutral_colors?.[100] || "#666",
                borderRadius: "12px",
                padding: "8px 16px",
                "&:hover": {
                  borderColor:
                    reduxTheme.neutral_colors?.[400] ||
                    "rgba(194, 147, 99, 0.3)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={onAccept}
              sx={{
                background: `linear-gradient(135deg, ${
                  reduxTheme.primary_colors?.[400] || "#614438"
                } 0%, ${
                  reduxTheme.primary_colors?.[300] || "#C29363"
                } 100%)`,
                textTransform: "none",
                borderRadius: "12px",
                padding: "8px 16px",
                color: reduxTheme.neutral_colors?.[100] || "#fff",
                "&:hover": {
                  background: `linear-gradient(135deg, ${
                    reduxTheme.primary_colors?.[500] || "#503629"
                  } 0%, ${
                    reduxTheme.primary_colors?.[400] || "#A67C52"
                  } 100%)`,
                },
              }}
            >
              Accept
            </Button>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
};

export default TermsModal;